import React, { useCallback } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { CollapsibleListSectionCollapsedContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content/collapsed-content';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { CollapsibleListSectionSubHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-sub-header';

import { MessageDescriptorPropType } from '../../../../prop-types/intl';
import { useOrderDetailsExpanderSection } from '../../fulfillment-plan/order-details-expanded-section-provider';

import style from './expander-section.module.less';
import sectionStyle from '../section.module.less';

const ExpanderSection = ({
  sectionId,
  headerContent,
  sectionContent,
  collapsedSectionContent,
  className,
  headerClassName,
  contentClassName,
  collapsedContentClassName,
  pendoTrackingId,
  subHeaderContent,
  subHeaderClassname,
  ariaLabelMessageDescriptor,
  isFullHeightWhenExpanded = false,
}) => {
  const { isExpanded, toggleIsExpanded } = useOrderDetailsExpanderSection(sectionId);
  const { formatMessage } = useIntl();
  const isFullHeightClassName = isFullHeightWhenExpanded && isExpanded ? style['full-height'] : undefined;

  const onSectionHeaderClick = useCallback(
    (e) => {
      if (!e?.defaultPrevented) {
        toggleIsExpanded();
      }
    },
    [toggleIsExpanded],
  );

  return (
    <section aria-label={formatMessage(ariaLabelMessageDescriptor)} className={isFullHeightClassName}>
      <CollapsibleListSection
        className={classnames(sectionStyle['order-details-section'], isFullHeightClassName, className)}
        open={isExpanded}
      >
        <CollapsibleListSectionHeader
          className={classnames(style.header, headerClassName)}
          contentClassName={style['header-content']}
          onClick={onSectionHeaderClick}
          pendoTrackingId={pendoTrackingId}
          theme='white'
        >
          {headerContent}
        </CollapsibleListSectionHeader>
        {subHeaderContent ? (
          <CollapsibleListSectionSubHeader className={classnames(style['sub-header'], subHeaderClassname)}>
            {subHeaderContent}
          </CollapsibleListSectionSubHeader>
        ) : (
          // This needs to be a fragment as opposed to false or null because of the way the <CollapsibleListSection> is written
          React.Fragment
        )}
        <CollapsibleListSectionContent className={classnames(style.content, contentClassName)}>
          {sectionContent}
        </CollapsibleListSectionContent>
        {collapsedSectionContent ? (
          <CollapsibleListSectionCollapsedContent className={classnames(style.content, collapsedContentClassName)}>
            {collapsedSectionContent}
          </CollapsibleListSectionCollapsedContent>
        ) : (
          // This needs to be a fragment as opposed to false or null because of the way the <CollapsibleListSection> is written
          React.Fragment
        )}
      </CollapsibleListSection>
    </section>
  );
};

export default ExpanderSection;

ExpanderSection.propTypes = {
  sectionId: PropTypes.string.isRequired,
  headerContent: PropTypes.node.isRequired,
  sectionContent: PropTypes.node.isRequired,
  collapsedSectionContent: PropTypes.node,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  collapsedContentClassName: PropTypes.string,
  pendoTrackingId: PropTypes.string,
  subHeaderContent: PropTypes.node,
  subHeaderClassname: PropTypes.string,
  ariaLabelMessageDescriptor: MessageDescriptorPropType.isRequired,
  isFullHeightWhenExpanded: PropTypes.bool,
};
